/**
 * Retouren v3 (sessie 172): de tijdlijn van één retour.
 *
 * Boven een baan met bolletjes en lijnen (alleen beeld), daaronder een
 * geordende lijst met tekst, zodat het ook zonder kleur leest. Maat:
 * mock-retouren-v3, desktop rechterhelft en iphone-dossier.
 */
import { Fragment } from "react";

import type { RetourV3Stap } from "@/lib/retouren-v3-api";

import "./dossier-v3.css";

function isBesluit(stap: RetourV3Stap): boolean {
  return stap.sleutel === "besluit";
}

function bolKlasse(stap: RetourV3Stap): string {
  if (stap.stand === "gedaan") return "rv3d-bol rv3d-bol-gedaan";
  if (stap.stand === "eind") return "rv3d-bol rv3d-bol-eind";
  if (stap.stand === "nu")
    return isBesluit(stap)
      ? "rv3d-bol rv3d-bol-nu rv3d-bol-besluit"
      : "rv3d-bol rv3d-bol-nu";
  return "rv3d-bol rv3d-bol-nog";
}

function stapKlasse(stap: RetourV3Stap): string | undefined {
  if (stap.stand === "nu")
    return isBesluit(stap) ? "rv3d-stap-besluit" : "rv3d-stap-nu";
  if (stap.stand === "eind") return "rv3d-stap-eind";
  return undefined;
}

/** Wat de stap is, voor wie de kleur niet ziet. */
function standWoord(stap: RetourV3Stap): string {
  if (stap.stand === "gedaan") return "gedaan";
  if (stap.stand === "nu") return "nu";
  if (stap.stand === "eind") return "einde";
  return "nog niet";
}

export function TijdlijnV3({
  stappen,
  smal,
}: {
  stappen: RetourV3Stap[];
  smal: boolean;
}) {
  return (
    <div
      className={smal ? "rv3d-tijdlijn rv3d-tijdlijn-smal" : "rv3d-tijdlijn"}
      data-testid="rv3d-tijdlijn"
    >
      <div className="rv3d-baan" aria-hidden="true">
        {stappen.map((stap, i) => (
          <Fragment key={stap.sleutel}>
            {i > 0 ? (
              <div
                className={
                  stap.stand === "nog_niet"
                    ? "rv3d-lijn rv3d-lijn-stip"
                    : "rv3d-lijn"
                }
                data-lijn={stap.stand === "nog_niet" ? "stip" : "vol"}
              />
            ) : null}
            <div className={bolKlasse(stap)} data-bol={stap.stand}>
              {stap.stand === "gedaan" ? "✓" : stap.stand === "eind" ? "✕" : null}
            </div>
          </Fragment>
        ))}
      </div>
      <ol aria-label="Tijdlijn" className="rv3d-stappen">
        {stappen.map((stap) => {
          const tekst = smal ? stap.kort : stap.label;
          const toonDatum = !smal && stap.datum;
          const woordNodig =
            stap.stand === "gedaan" || stap.stand === "eind" || !toonDatum;
          return (
            <li
              key={stap.sleutel}
              className={stapKlasse(stap)}
              data-stap={stap.sleutel}
              data-stand={stap.stand}
            >
              {tekst}
              {toonDatum ? (
                <>
                  <br />
                  {stap.datum}
                </>
              ) : null}
              {woordNodig ? (
                <span className="rv3d-verborgen">, {standWoord(stap)}</span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
